import { Dispatch, RefObject, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DeckGLRef } from '@deck.gl/react/typed';
import { PickingInfo, WebMercatorViewport } from '@deck.gl/core/typed';
import { ViewStateChangeParameters } from '@deck.gl/core/typed/controllers/controller';
import { getSharedCampaignPlanMetaData } from 'store/sharedCampaignPlan/selectors';
import { CardPosition, ViewState } from 'components/pages/Planner/PlannerMap/PlannerMap.types';
import { clearClusterSelection } from 'store/carto/reducer';
import { useSelectionCardStickyPosition } from 'components/pages/Planner/PlannerMap/useSelectionCardStickyPosition';
import { isObjectClickable } from 'components/pages/Planner/PlannerMap/utils/getIsMapElementClickable';
import { DirectSalesAsset } from 'components/common/types/DirectSalesCampaign.types';

interface HoveredFurnitureTooltip {
  values: string[];
  coordinates: number[];
}

interface UseCampaignPlanMapState {
  viewState: ViewState;
  deckRef: RefObject<DeckGLRef> | null;
  cardPosition: CardPosition;
  hoveredFurnitureTooltip: HoveredFurnitureTooltip | null;
  furnitureTooltipPosition: { x: number; y: number } | null;
  updateMapZoom: (zoom: number) => void;
  onViewStateChange: (params: ViewStateChangeParameters) => void;
  onMapClick: (info: PickingInfo) => void;
  onMapHover: (info: PickingInfo) => void;
  getMapCursor: (params: { isHovering?: boolean }) => string;
  setBoundingBox: Dispatch<SetStateAction<string[]>>;
  handleAssetSearchSelect: (coordinate: [number, number]) => void;
  handleDeckAfterRender: () => void;
}

export const useCampaignPlanMapState = (containerSize: {
  width: number | null;
  height: number | null;
}): UseCampaignPlanMapState => {
  const dispatch = useDispatch();
  const { latitude, longitude } = useSelector(getSharedCampaignPlanMetaData);

  const [viewState, setViewState] = useState<ViewState>({
    longitude: Number(longitude) || 0,
    latitude: Number(latitude) || 0,
    zoom: 5,
    maxZoom: 20,
    pitch: 0,
    bearing: 0,
    width: 400,
    height: 400,
  });

  const [boundingBox, setBoundingBox] = useState<string[]>([]);
  const [hoveredFurnitureTooltip, setHoveredFurnitureTooltip] = useState<HoveredFurnitureTooltip | null>(null);
  const pendingSearchCoordinate = useRef<[number, number] | null>(null);

  const { onClusterClick, onViewportChange, cardPosition, deckRef } = useSelectionCardStickyPosition({
    viewState,
    setViewState,
  });

  const updateMapZoom = (zoom: number): void => {
    if (zoom >= 20 || zoom <= 1) return;
    setViewState((prev) => ({ ...prev, zoom }));
  };

  const onViewStateChange = useCallback(
    ({ viewState: newViewState }: ViewStateChangeParameters) => {
      setViewState(newViewState as ViewState);
      onViewportChange({ viewState: newViewState } as ViewStateChangeParameters);
    },
    [onViewportChange],
  );

  const onMapClick = useCallback(
    (info: PickingInfo): void => {
      if (isObjectClickable(info.object)) {
        onClusterClick(info);
      } else {
        dispatch(clearClusterSelection());
      }
    },
    [dispatch, onClusterClick],
  );

  const getMapCursor = useCallback(({ isHovering }: { isHovering?: boolean }): string => {
    if (isHovering) return 'pointer';
    return '';
  }, []);

  const { width, height } = containerSize;

  useEffect(() => {
    if (!boundingBox.length || !width || !height) return;

    const [minLat, maxLat, minLon, maxLon] = boundingBox.map(Number);
    const viewport = new WebMercatorViewport({ width, height });
    const fittedViewport = viewport.fitBounds(
      [
        [minLon, minLat],
        [maxLon, maxLat],
      ],
      { padding: 20 },
    );

    setViewState((prev) => ({
      ...prev,
      longitude: fittedViewport.longitude,
      latitude: fittedViewport.latitude,
      zoom: fittedViewport.zoom,
    }));
  }, [boundingBox]);

  const furnitureTooltipPosition = useMemo(() => {
    if (!hoveredFurnitureTooltip) return null;

    const viewport = new WebMercatorViewport({
      ...viewState,
      width: width || viewState.width,
      height: height || viewState.height,
    });

    const [x, y] = viewport.project(hoveredFurnitureTooltip.coordinates);
    return { x, y };
  }, [hoveredFurnitureTooltip, viewState, width, height]);

  const onMapHover = useCallback((info: PickingInfo): void => {
    if (isObjectClickable(info.object)) {
      const isCluster = info.object.assets.length > 0;

      setHoveredFurnitureTooltip({
        values: isCluster
          ? Array.from(
              new Set(
                info.object.assets.map((asset: DirectSalesAsset & { furnitureCode: string }) => asset.furnitureCode),
              ),
            )
          : [info.object.furnitureCode],
        coordinates: info.coordinate || [info.object.longitude, info.object.latitude],
      });
    } else {
      setHoveredFurnitureTooltip(null);
    }
  }, []);

  const handleAssetSearchSelect = useCallback((coordinate: [number, number]) => {
    pendingSearchCoordinate.current = coordinate;
  }, []);

  const handleDeckAfterRender = useCallback(() => {
    if (!pendingSearchCoordinate.current) return;

    const viewport = deckRef?.current?.deck?.getViewports()[0];
    if (!viewport) return;

    const [x, y] = viewport.project(pendingSearchCoordinate.current);
    const pickInfo = deckRef?.current?.deck?.pickObject({ x, y });

    if (pickInfo && isObjectClickable(pickInfo.object)) {
      pendingSearchCoordinate.current = null;
      onClusterClick(pickInfo);
    }
  }, [deckRef, onClusterClick]);

  return {
    viewState,
    deckRef,
    cardPosition,
    hoveredFurnitureTooltip,
    furnitureTooltipPosition,
    updateMapZoom,
    onViewStateChange,
    onMapClick,
    onMapHover,
    getMapCursor,
    setBoundingBox,
    handleAssetSearchSelect,
    handleDeckAfterRender,
  };
};
